import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, View, Text, StyleSheet, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';
import { AppDispatch, RootState } from '../../../store';
import { setReportConfigurationRows, ReportConfigRow } from '../../../store/slices/reportingConfigurationSlice';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { locationApi } from '../../Admin/pages/LocationManagement/locationApi';
import reportingConfigurationApi from '../../Admin/pages/ReportingConfiguration/reportingConfigurationApi';
import KPIOverviewTab from './KPIOverview/KPIOverviewTab';
import ReportsTab from './Reports/ReportsTab';
import KPIOnePagerV2Tab from './KPIOnePagerV2/KPIOnePagerV2Tab';
import { useAdminLocations } from '../hooks/useAdminLocations';
import { TimeFrameOption } from '../components/TimeFrameSelector';
import { DateSelectionState } from '../types/dateSelectionTypes';
import { getApiDateRange } from '../utils/dateFilterHelpers';
import type { OnePagerFilters } from '../types/onePagerTypes';

interface AnalyticsHubDashBoardProps {
	onOpenReportPage?: (reportId: string) => void;
	/** When provided, the dashboard opens directly on this tab instead of 'kpi-overview'. */
	initialTab?: AnalyticsHubTab;
}

interface LHDropdownItem {
	id: string | number;
	displayText: string;
	[key: string]: any;
}

type AnalyticsHubTab = 'kpi-overview' | 'all-reports' | 'kpi-one-pager-v2';

const normalizeFilterValue = (value: unknown): string => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const UNKNOWN_LOCATION_TYPE_KEY = 'unknown';

const getNormalizedLocationFacilityType = (value: unknown): string => {
	const normalized = normalizeFilterValue(value);
	return normalized || UNKNOWN_LOCATION_TYPE_KEY;
};

// Hardcoded in-app report entries that are always injected unless the backend
// already contains a row with the same link (prevents duplicates if backend
// later adds them officially).
const HARDCODED_INAPP_ROWS: ReportConfigRow[] = [
	// {
	// 	id: 'on-time-dispatch',
	// 	reportId: null,
	// 	name: 'On Time Dispatch',
	// 	primaryTag: 'Reliability',
	// 	secondaryTags: ['Navigator'],
	// 	kpiTags: ['OTD', 'on-time dispatch', 'completed loads', 'incomplete loads'],
	// 	status: 'Active',
	// 	description: 'On-time dispatch completion rates, incomplete loads, and driver arrival metrics by location and timeframe.',
	// 	link: '/navigator-reports/on-time-dispatch',
	// 	icon: 'local-shipping',
	// 	reportType: 'INTERNAL_REDIRECT',
	// },
];

const AnalyticsHubDashBoard: React.FC<AnalyticsHubDashBoardProps> = ({ onOpenReportPage, initialTab }) => {
	const dispatch = useDispatch<AppDispatch>();
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const { width } = useWindowDimensions();
	const isMobileLayout = width < 768;

	// ─── Read the header's selected location from Redux (set by DLOHeader) ──
	const headerSelectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);
	// Derive initial location from header immediately so API calls fire on first render
	const initialLocationFromHeader = useMemo<LHDropdownItem[]>(() => {
		if (!headerSelectedLocation) return [];
		const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
		const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';
		if (!locId && !locName) return [];
		return [{ id: locId, displayText: locName }];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only compute once on mount — don't react to later header changes
	const hasAutoPopulatedRef = useRef(initialLocationFromHeader.length > 0);

	// ─── Report Configuration (fetched once on dashboard load) ──────────────
	const [reportConfigRows, setLocalReportConfigRows] = useState<ReportConfigRow[]>([]);
	const [isLoadingReportConfig, setIsLoadingReportConfig] = useState(false);

	useEffect(() => {
		let isMounted = true;

		const loadReportConfiguration = async () => {
			setIsLoadingReportConfig(true);
			try {
				const apiRows = await reportingConfigurationApi.getAll();
				if (!isMounted) return;
				// Inject hardcoded in-app rows that are not already present in the API response
				const existingLinks = new Set(apiRows.map((r) => (r.link || '').trim().toLowerCase()));
				const rowsToInject = HARDCODED_INAPP_ROWS.filter(
					(r) => !existingLinks.has(r.link.toLowerCase())
				);
				const mergedRows = [...apiRows, ...rowsToInject];
				setLocalReportConfigRows(mergedRows);
				dispatch(setReportConfigurationRows(mergedRows));
			} catch (error) {
				if (!isMounted) return;
				console.error('[AnalyticsHubDashBoard] Failed to load report configurations:', error);
				// Still inject hardcoded rows even when the API fails
				setLocalReportConfigRows(HARDCODED_INAPP_ROWS);
				dispatch(setReportConfigurationRows(HARDCODED_INAPP_ROWS));
			} finally {
				if (isMounted) setIsLoadingReportConfig(false);
			}
		};

		void loadReportConfiguration();

		return () => { isMounted = false; };
	}, [dispatch]);

	const [regionOptions, setRegionOptions] = useState<LHDropdownItem[]>([]);
	const [marketOptions, setMarketOptions] = useState<LHDropdownItem[]>([]);
	const [region, setRegion] = useState<LHDropdownItem | null>(null);
	const [market, setMarket] = useState<LHDropdownItem | null>(null);
	const [selectedMarkets, setSelectedMarkets] = useState<LHDropdownItem[]>([]);
	const [selectedRegions, setSelectedRegions] = useState<LHDropdownItem[]>([]);
	const selectedRegionId = normalizeFilterValue(region?.id ?? '');
	const selectedRegionIds: string[] = useMemo(() => {
		// Empty selection means "all" (no filter applied)
		if (selectedRegions.length === 0) return ['all'];
		return selectedRegions.map(r => normalizeFilterValue(r.id));
	}, [selectedRegions]);
	const selectedMarketIds: string[] = useMemo(() => {
		// Empty selection means "all" (no filter applied)
		if (selectedMarkets.length === 0) return ['all'];
		return selectedMarkets.map(m => normalizeFilterValue(m.id));
	}, [selectedMarkets]);

	const { locations: adminLocations } = useAdminLocations({ pageSize: 500 });

	// ─── Location Type state & derivation ────────────────────────────────────
	const [selectedLocationTypes, setSelectedLocationTypes] = useState<LHDropdownItem[]>([]);
	const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrameOption | null>(null);
	const [dateSelection, setDateSelection] = useState<DateSelectionState>({ mode: 'SINGLE_DATE' });
	const [isSharedMobileFilterOverlayVisible, setSharedMobileFilterOverlayVisible] = useState(false);

	// Locations filtered by Region + Market (before Location Type filter)
	const regionMarketFilteredLocations = useMemo(
		() => adminLocations.filter(loc => {
			const matchesRegion =
				selectedRegionIds.includes('all') || selectedRegionIds.includes(normalizeFilterValue(loc.region));
			const matchesMarket =
				selectedMarketIds.includes('all') || selectedMarketIds.includes(normalizeFilterValue(loc.market));
			return matchesRegion && matchesMarket;
		}),
		[adminLocations, selectedRegionIds, selectedMarketIds]
	);

	// Location Type options are derived from the currently available locations,
	// excluding empty values and duplicates.
	const locationTypeOptions: LHDropdownItem[] = useMemo(() => {
		const byNormalizedValue = new Map<string, string>();

		for (const loc of regionMarketFilteredLocations) {
			const rawType = String(loc.locationFacilityType ?? '').trim();
			const normalized = getNormalizedLocationFacilityType(rawType);
			const displayText = normalized === UNKNOWN_LOCATION_TYPE_KEY ? 'Unknown' : rawType;

			// Keep the first encountered display value per normalized key.
			if (!byNormalizedValue.has(normalized)) {
				byNormalizedValue.set(normalized, displayText);
			}
		}

		return Array.from(byNormalizedValue.entries())
			.map(([normalized, displayText]) => ({ id: normalized, displayText }))
			.sort((a, b) => a.displayText.localeCompare(b.displayText));
	}, [regionMarketFilteredLocations]);

	// Derive selected location type IDs for filtering
	const selectedLocationTypeIds: string[] = useMemo(() => {
		if (selectedLocationTypes.length === 0) return ['all'];
		return selectedLocationTypes.map(lt => normalizeFilterValue(lt.id));
	}, [selectedLocationTypes]);

	// Final location options: Region + Market + Location Type filtered
	const locationOptions: LHDropdownItem[] = useMemo(
		() => regionMarketFilteredLocations
			.filter(loc => {
				if (selectedLocationTypeIds.includes('all')) return true;
				return selectedLocationTypeIds.includes(getNormalizedLocationFacilityType(loc.locationFacilityType));
			})
			.map((loc: { locId: any; locName: any; }) => ({ id: loc.locId, displayText: loc.locName }))
			.sort((a, b) => a.displayText.localeCompare(b.displayText)),
		[regionMarketFilteredLocations, selectedLocationTypeIds]
	);

	// Clear invalid location type selections when region/market changes
	useEffect(() => {
		if (selectedLocationTypes.length === 0) return;
		const validTypes = selectedLocationTypes.filter(lt =>
			locationTypeOptions.some(opt => normalizeFilterValue(opt.id) === normalizeFilterValue(lt.id))
		);
		if (validTypes.length !== selectedLocationTypes.length) {
			setSelectedLocationTypes(validTypes);
		}
	}, [locationTypeOptions]);

	const onLocationTypeChange = useCallback((value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			setSelectedLocationTypes(value);
		} else if (value) {
			setSelectedLocationTypes([value]);
		} else {
			setSelectedLocationTypes([]);
		}
	}, []);

	const yearOptions: LHDropdownItem[] = useMemo(() => {
		const currentYear = new Date().getFullYear();
		return Array.from({ length: 4 }, (_, i) => ({
			id: String(currentYear - i),
			displayText: String(currentYear - i),
		}));
	}, []);

	const periodOptions: LHDropdownItem[] = useMemo(
		() => Array.from({ length: 13 }, (_, i) => {
			const value = `P${i + 1}`;
			return { id: value, displayText: value };
		}),
		[]
	);

	// Week options are intentionally empty as a static fallback — the actual week options
	// (with correct weekInYear IDs) are derived from the API via usePeriodWeeks in KPIOverviewTab.
	// Static W1-W4 values don't carry meaningful week-in-year information.
	const weekOptions: LHDropdownItem[] = useMemo(() => [], []);

	const [location, setLocation] = useState<LHDropdownItem[]>(initialLocationFromHeader);
	const [year, setYear] = useState<LHDropdownItem | null>(yearOptions[0]);
	const [selectedYears, setSelectedYears] = useState<LHDropdownItem[]>(yearOptions[0] ? [yearOptions[0]] : []);
	const [selectedPeriods, setSelectedPeriods] = useState<LHDropdownItem[]>([]);
	const [selectedWeeks, setSelectedWeeks] = useState<LHDropdownItem[]>([]);
	// Derived single-select values for backward compatibility with components expecting single item
	const period: LHDropdownItem | null = selectedPeriods.length > 0 ? selectedPeriods[selectedPeriods.length - 1] : null;
	const weekInYear: LHDropdownItem | null = selectedWeeks.length > 0 ? selectedWeeks[selectedWeeks.length - 1] : null;
	const [activeTab, setActiveTab] = useState<AnalyticsHubTab>(initialTab ?? 'kpi-overview');
	useEffect(() => {
		if (initialTab) {
			setActiveTab(initialTab);
		}
	}, [initialTab]);
	// Date range derived directly from the lifted dateSelection state.
	// Eliminates the previous indirection via onDateRangeChange callback which
	// was not always connected (e.g. the One Pager tab's KPIOverviewTab instance).
	const dateApiRange = useMemo(() => getApiDateRange(dateSelection), [dateSelection]);

	// ─── One Pager V2 filters: snapshot only committed on Apply/Reset ───────
	// The One Pager V2 API must only be called when the user clicks Apply (or Reset),
	// not on every intermediate filter change. Since some filters (Region/Market/
	// Location Type) propagate to this component's state immediately (for cascading
	// purposes) while others are draft-gated inside KPIOverviewTab, we can't rely on
	// the live state above being fully "committed" at all times. Instead, KPIOverviewTab
	// calls onApplyFilters() at the end of its Apply/Reset handlers (once its own state
	// has settled), which bumps this counter. The effect below then re-reads the latest
	// live filter values on the next render (avoiding stale closures from React's state
	// batching) and snapshots them into appliedOnePagerFilters — the only filters object
	// actually passed down to useOnePager via KPIOnePagerV2Tab.
	const [onePagerApplyTrigger, setOnePagerApplyTrigger] = useState(0);
	const handleOnePagerFiltersCommit = useCallback(() => {
		setOnePagerApplyTrigger(t => t + 1);
	}, []);

	const buildOnePagerFilters = useCallback((): OnePagerFilters => ({
		region: selectedRegions.map(r => String(r.id)),
		market: selectedMarkets.map(m => String(m.id)),
		location: location.map(l => String(l.id)),
		locationType: selectedLocationTypes.map(lt => String(lt.id)),
		year: selectedYears.map(y => String(y.id)),
		period: selectedPeriods.map(p => Number(String(p.id).replace(/\D/g, ''))).filter(n => !isNaN(n)),
		week: selectedWeeks.map(w => Number(String(w.id).replace(/\D/g, ''))).filter(n => !isNaN(n)),
		startDate: dateApiRange.startDate,
		endDate: dateApiRange.endDate,
	}), [selectedRegions, selectedMarkets, location, selectedLocationTypes, selectedYears, selectedPeriods, selectedWeeks, dateApiRange]);

	const [appliedOnePagerFilters, setAppliedOnePagerFilters] = useState<OnePagerFilters>(() => buildOnePagerFilters());

	useEffect(() => {
		setAppliedOnePagerFilters(buildOnePagerFilters());
		// Only re-snapshot when Apply/Reset fires — intentionally excludes buildOnePagerFilters
		// and the live filter values from deps so intermediate draft changes don't trigger a fetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onePagerApplyTrigger]);
	const [isLoadingRegions, setIsLoadingRegions] = useState(false);
	// Filter pushed into the Reports tab when the user clicks a KPI card's Power BI / Tableau
	// badge from the KPI Overview tab. `null` means “no filter”. Cleared automatically when
	// the user navigates away from the Reports tab.
	const [reportsPreFilter, setReportsPreFilter] = useState<string | null>(null);
	const hasRegionSelection = !selectedRegionIds.includes('all');
	const hasMarketSelection = !selectedMarketIds.includes('all');
	const hasRegionApiResult = regionOptions.length > 0;
	const hasMatchingLocations = locationOptions.length > 0;
	// Only show "no match" when user has explicitly selected both region AND market but no locations exist
	const isNoLocationMatch = hasRegionSelection && hasMarketSelection && !hasMatchingLocations;
	// Market is always enabled — all distinct markets are loaded on page load
	const isMarketDisabled = false;
	// Location is disabled only when there's a genuine "no match" scenario
	const isLocationDisabled = isNoLocationMatch;
	const locationPlaceholder = useMemo(() => {
		if (isNoLocationMatch) return 'No locations match selected Region / Market';
		return 'Select...';
	}, [isNoLocationMatch]);

	useEffect(() => {
		let isMounted = true;

		const loadRegions = async () => {
			setIsLoadingRegions(true);
			try {
				const regions = await locationApi.getRegions();
				if (!isMounted) {
					return;
				}

				const options = regions
						.filter(regionName => regionName?.trim().toLowerCase() !== 'all')
						.map(regionName => ({ id: regionName, displayText: regionName }));
				setRegionOptions(options);
				setRegion(prev => {
					if (prev && options.some(option => String(option.id) === String(prev.id))) {
						return prev;
					}
					return null;
				});
			} catch {
				if (!isMounted) {
					return;
				}
				setRegionOptions([]);
				setRegion(null);
			} finally {
				if (isMounted) {
					setIsLoadingRegions(false);
				}
			}
		};

		loadRegions();

		return () => {
			isMounted = false;
		};
	}, []);

	// ─── Fetch all distinct markets on page load ─────────────────────────────
	useEffect(() => {
		let isMounted = true;

		const loadAllMarkets = async () => {
			try {
				const api = createApiClient();
				const response = await apiClient(api, '/admin/locations/markets', 'GET');
				if (!isMounted) return;

				const marketNames: string[] = Array.isArray(response?.data) ? response.data : [];
				const options = marketNames
					.filter(name => name?.trim().toLowerCase() !== 'all')
					.map(name => ({ id: name, displayText: name }));
				setMarketOptions(options);
			} catch {
				if (!isMounted) return;
				setMarketOptions([]);
			}
		};

		loadAllMarkets();

		return () => { isMounted = false; };
	}, []);


	const selectedRegion = region?.displayText ?? String(region?.id ?? '');

	useEffect(() => {
		if (isNoLocationMatch) {
			setLocation([]);
			return;
		}

		// Don't clear location if options haven't loaded yet — preserve the
		// initial value from the header so API calls can fire immediately.
		if (locationOptions.length === 0) return;
		
		setLocation(prev => {
			// Filter to keep only items that still exist in options (match by id OR displayText)
			const validItems = prev.filter(selectedLoc =>
				locationOptions.some(option =>
					String(option.id) === String(selectedLoc.id) ||
					option.displayText === selectedLoc.displayText
				)
			);
			
			// If user had valid selections, keep them
			if (validItems.length > 0) {
				return validItems;
			}
			
			// No valid selections — return empty (means "all")
			return [];
		});
	}, [locationOptions, isNoLocationMatch]);

	// ─── Auto-populate location from header on initial page load ─────────────
	// Phase 1: immediately seed from Redux before locationOptions loads
	useEffect(() => {
		if (hasAutoPopulatedRef.current || location.length > 0 || !headerSelectedLocation) return;
		const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
		const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';
		if (!locId && !locName) return;
		setLocation([{ id: locId, displayText: locName }]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [headerSelectedLocation]);

	// Phase 2: refine to canonical locId once locationOptions loads
	useEffect(() => {
		if (hasAutoPopulatedRef.current) return;
		if (!headerSelectedLocation) return;
		if (locationOptions.length === 0) return;

		const locId = String(
			headerSelectedLocation.id || headerSelectedLocation.locationId || ''
		);
		const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';

		const matchingOption = locationOptions.find(
			opt => String(opt.id) === locId || opt.displayText === locName
		);

		if (matchingOption) {
			setLocation([matchingOption]);
			hasAutoPopulatedRef.current = true;
			// Commit filters so the One Pager fetches with the canonical location ID
			handleOnePagerFiltersCommit();
		}
	}, [headerSelectedLocation, locationOptions, handleOnePagerFiltersCommit]);

	useEffect(() => {
		if (!selectedRegion || selectedRegionId === '' || selectedRegionId === 'all') {
			setMarketOptions([]);
			setMarket(null);
			return;
		}

		let isMounted = true;

		const loadMarketsByRegion = async () => {
			try {
				const markets = await locationApi.getMarketsByRegion(selectedRegion);
				if (!isMounted) {
					return;
				}

				const options = markets
						.filter(marketName => marketName?.trim().toLowerCase() !== 'all')
						.map(marketName => ({ id: marketName, displayText: marketName }));
				setMarketOptions(options);
				setMarket(prev => {
					if (prev && options.some(option => String(option.id) === String(prev.id))) {
						return prev;
					}
					return null;
				});
			} catch {
				if (!isMounted) {
					return;
				}
				setMarketOptions([]);
				setMarket(null);
			}
		};

		loadMarketsByRegion();

		return () => {
			isMounted = false;
		};
	}, [selectedRegion, selectedRegionId]);

	const tabs = useMemo(
		() => [
			{ key: 'kpi-overview' as const, label: 'KPI Overview' },
			{ key: 'kpi-one-pager-v2' as const, label: 'KPI One Pager' },
			{ key: 'all-reports' as const, label: 'Reports' },
		],
		[]
	);

	const pickSingle = (value: LHDropdownItem | LHDropdownItem[] | null): LHDropdownItem | null => {
		if (!value || Array.isArray(value)) {
			return null;
		}
		return value;
	};

	const onRegionChange = (value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			// Multi-select: store full array for location filtering (empty = all)
			setSelectedRegions(value);
			// Keep single region for backwards compat (use last selected)
			const lastSelected = value.length > 0 ? value[value.length - 1] : null;
			setRegion(lastSelected);
		} else {
			setRegion(value);
			setSelectedRegions(value ? [value] : []);
		}
		setMarket(null);
		setSelectedMarkets([]);
		// Clear locations and location types whenever region changes
		setLocation([]);
		setSelectedLocationTypes([]);
	};
	const onMarketChange = (value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			// Multi-select: store full array for location filtering (empty = all)
			setSelectedMarkets(value);
			// Keep single market for backwards compat (use last selected)
			setMarket(value.length > 0 ? value[value.length - 1] : null);
		} else {
			setMarket(value);
			setSelectedMarkets(value ? [value] : []);
		}
		// Clear locations and location types whenever market changes
		setLocation([]);
		setSelectedLocationTypes([]);
	};
	const onLocationChange = (value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			setLocation(value ?? []);
		} else if (value) {
			setLocation([value]);
		} else {
			setLocation([]);
		}
	};
	const onYearChange = (value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			setSelectedYears(value);
			setYear(value.length > 0 ? value[value.length - 1] : null);
		} else if (value) {
			setSelectedYears([value]);
			setYear(value);
		} else {
			setSelectedYears([]);
			setYear(null);
		}
	};
	const onPeriodChange = (value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			setSelectedPeriods(value);
		} else if (value) {
			setSelectedPeriods([value]);
		} else {
			setSelectedPeriods([]);
		}
	};
	const onWeekInYearChange = (value: LHDropdownItem | LHDropdownItem[] | null) => {
		if (Array.isArray(value)) {
			setSelectedWeeks(value);
		} else if (value) {
			setSelectedWeeks([value]);
		} else {
			setSelectedWeeks([]);
		}
	};

	const onExport = () => {
		// Placeholder action until export service is integrated.
		console.log('[AnalyticsHub] Export clicked', { region, market, location, year, period, weekInYear });
	};

	const onRefresh = () => {
		// Placeholder action until refresh service is integrated.
		console.log('[AnalyticsHub] Refresh clicked', { region, market, location, year, period, weekInYear });
	};

	const handleOpenReport = (reportId: string) => {
		onOpenReportPage?.(reportId);
	};

	// Triggered from a KPI card's Power BI / Tableau badge in the KPI Overview tab.
	// Switches to the Reports tab and seeds it with the KPI name as a search filter.
	// The KPI title is sanitized to strip any trailing display symbols (e.g. "%", "$",
	// "()", whitespace) so e.g. "Rapid Breaks %" is filtered as "Rapid Breaks".
	// Internal punctuation/spacing is preserved to keep existing matching behavior intact.
	const handleNavigateToReports = useCallback((kpiName: string) => {
		const sanitized = kpiName.replace(/[^A-Za-z0-9]+$/u, '').trim();
		setReportsPreFilter(sanitized || kpiName);
		setActiveTab('all-reports');
	}, []);

	// Whenever the user navigates away from the Reports tab, drop the pre-applied
	// filter so the catalog returns to its default state on the next visit.
	useEffect(() => {
		if (activeTab !== 'all-reports' && reportsPreFilter !== null) {
			setReportsPreFilter(null);
		}
	}, [activeTab, reportsPreFilter]);

	const handleMobileFilterOverlayVisibleChange = useCallback((visible: boolean) => {
		setSharedMobileFilterOverlayVisible(visible);
		// When overlay closes from inside KPIOverviewTab, sync local trigger
		if (!visible) setMobileFilterOpen(false);
	}, []);

	// ─── Mobile filter button state (above tabs, mobile only) ──────────────
	const isMobileDevice = Platform.OS !== 'web' && width < 768;
	const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

	// Count how many filter categories have a non-default (non-empty) selection
	const appliedFilterCount = useMemo(() => {
		let count = 0;
		if (selectedYears.length > 0) count++;
		if (selectedRegions.length > 0) count++;
		if (selectedMarkets.length > 0) count++;
		if (selectedLocationTypes.length > 0) count++;
		if (location.length > 0) count++;
		if (selectedPeriods.length > 0) count++;
		if (selectedWeeks.length > 0) count++;
		if (dateSelection.mode !== 'SINGLE_DATE' || dateSelection.selectedDate) count++;
		return count;
	}, [year, selectedRegions, selectedMarkets, selectedLocationTypes, location, selectedPeriods, selectedWeeks, dateSelection]);

	const showMobileFilterButton = isMobileDevice && activeTab !== 'all-reports';

	return (
		<View style={[styles.page, isDark && { backgroundColor: colors.background.page }]}>
			<ScrollView
				style={styles.section}
				contentContainerStyle={styles.sectionContentNoPadding}
				showsVerticalScrollIndicator={false}
			>
				<View style={styles.sectionContent}>
					<View style={styles.headerRow}>
						<View>
							<View style={styles.titleRow}>
								<MaterialIcons name="analytics" size={24} color="#005CBC" />
								<Text style={[styles.title, isDark && { color: colors.text.primary }]}>Analytics Hub</Text>
							</View>
							<Text style={[styles.subtitle, isDark && { color: colors.text.secondary }]}>All reporting and KPI data across Warehouse DSD operations</Text>
						</View>

						{/* <View style={styles.actionsRow}>
							<Pressable style={[styles.actionButton, styles.exportButton]} onPress={onExport}>
								<MaterialIcons name="file-download" size={18} color="#0050A6" />
								<Text style={styles.exportButtonText}>Export</Text>
							</Pressable>

							<Pressable style={[styles.actionButton, styles.refreshButton]} onPress={onRefresh}>
								<MaterialIcons name="refresh" size={18} color="#FFFFFF" />
								<Text style={styles.refreshButtonText}>Refresh</Text>
							</Pressable>
						</View> */}
					</View>

					{showMobileFilterButton && (
						<TouchableOpacity
							style={[styles.mobileFilterBtn, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}
							onPress={() => setMobileFilterOpen(prev => !prev)}
							activeOpacity={0.7}
							accessibilityRole="button"
							accessibilityLabel={`Filters, ${appliedFilterCount} active`}
						>
							<MaterialIcons name="filter-list" size={18} color={isDark ? colors.text.primary : '#2D2D2D'} />
							<Text style={[styles.mobileFilterBtnText, isDark && { color: colors.text.primary }]}>Filters</Text>
							<View style={styles.mobileFilterBadge}>
								<Text style={styles.mobileFilterBadgeText}>{appliedFilterCount}</Text>
							</View>
						</TouchableOpacity>
					)}
				</View>

				<ScrollView
					horizontal
					showsHorizontalScrollIndicator={false}
					style={[styles.tabBar, isDark && { backgroundColor: colors.background.page, borderBottomColor: colors.border.default }]}
					contentContainerStyle={styles.tabBarContent}
				>
					{tabs.map((tab) => {
						const isActive = activeTab === tab.key;
						return (
							<TouchableOpacity
								key={tab.key}
								style={[styles.tab, isActive && styles.tabActive]}
								onPress={() => setActiveTab(tab.key)}
								activeOpacity={0.7}
							>
								<Text style={[styles.tabLabel, isDark && { color: colors.text.secondary }, isActive && styles.tabLabelActive]}>
									{tab.label}
								</Text>
							</TouchableOpacity>
						);
					})}
				</ScrollView>

				<View style={[styles.sectionContent, styles.tabContent]}>
					{activeTab === 'kpi-overview' && (
						<KPIOverviewTab
							regionOptions={regionOptions}
							marketOptions={marketOptions}
							locationOptions={locationOptions}
							locationTypeOptions={locationTypeOptions}
							selectedLocationTypes={selectedLocationTypes}
							onLocationTypeChange={onLocationTypeChange}
							yearOptions={yearOptions}
							periodOptions={periodOptions}
							weekOptions={weekOptions}
							region={region}
							market={market}
							location={location}
							year={year}
							period={period}
							weekInYear={weekInYear}
							onRegionChange={onRegionChange}
							onMarketChange={onMarketChange}
							onLocationChange={onLocationChange}
							onYearChange={onYearChange}
							onPeriodChange={onPeriodChange}
							onWeekInYearChange={onWeekInYearChange}
							isMarketDisabled={isMarketDisabled}
							isLocationDisabled={isLocationDisabled}
							locationPlaceholder={locationPlaceholder}
							showDashboardContent
							onNavigateToReports={handleNavigateToReports}
							selectedTimeFrame={selectedTimeFrame}
							onSelectedTimeFrameChange={setSelectedTimeFrame}
							dateSelection={dateSelection}
							onDateSelectionChange={setDateSelection}
							onMobileFilterOverlayVisibleChange={handleMobileFilterOverlayVisibleChange}
							onApplyFilters={handleOnePagerFiltersCommit}
							mobileFilterOverlayOpen={mobileFilterOpen}
							hideMobileFilterButton={isMobileDevice}
							selectedRegions={selectedRegions}
							selectedMarkets={selectedMarkets}
							selectedYears={selectedYears}
							selectedPeriods={selectedPeriods}
							selectedWeeks={selectedWeeks}
						/>
					)}
	
					{activeTab === 'all-reports' && (
						<ReportsTab
							onOpenReport={handleOpenReport}
							isReportConfigLoading={isLoadingReportConfig}
							preAppliedFilter={reportsPreFilter}
						/>
					)}

					{activeTab === 'kpi-one-pager-v2' && (
						<View style={[styles.kpiOnePagerFullBleed, isMobileLayout && styles.kpiOnePagerFullBleedMobile]}>
							<KPIOverviewTab
								regionOptions={regionOptions}
								marketOptions={marketOptions}
								locationOptions={locationOptions}
								locationTypeOptions={locationTypeOptions}
								selectedLocationTypes={selectedLocationTypes}
								onLocationTypeChange={onLocationTypeChange}
								yearOptions={yearOptions}
								periodOptions={periodOptions}
								weekOptions={weekOptions}
								region={region}
								market={market}
								location={location}
								year={year}
								period={period}
								weekInYear={weekInYear}
								onRegionChange={onRegionChange}
								onMarketChange={onMarketChange}
								onLocationChange={onLocationChange}
								onYearChange={onYearChange}
								onPeriodChange={onPeriodChange}
								onWeekInYearChange={onWeekInYearChange}
								isMarketDisabled={isMarketDisabled}
								isLocationDisabled={isLocationDisabled}
								locationPlaceholder={locationPlaceholder}
								showDashboardContent={false}
								mobileFilterButtonRightAligned
								onNavigateToReports={handleNavigateToReports}
								selectedTimeFrame={selectedTimeFrame}
								onSelectedTimeFrameChange={setSelectedTimeFrame}
								dateSelection={dateSelection}
								onDateSelectionChange={setDateSelection}
								onMobileFilterOverlayVisibleChange={handleMobileFilterOverlayVisibleChange}
								onApplyFilters={handleOnePagerFiltersCommit}
								mobileFilterOverlayOpen={mobileFilterOpen}
								hideMobileFilterButton={isMobileDevice}
								selectedRegions={selectedRegions}
								selectedMarkets={selectedMarkets}
								selectedYears={selectedYears}
								selectedPeriods={selectedPeriods}
								selectedWeeks={selectedWeeks}
							/>
							{!isSharedMobileFilterOverlayVisible && (
								<KPIOnePagerV2Tab
									filters={appliedOnePagerFilters}
								/>
							)}
						</View>
					)}
				</View>
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	page: {
		flex: 1,
		backgroundColor: '#f5f9ff',
	},
	section: {
		flex: 1,
	},
	sectionContent: {
		paddingHorizontal: 24,
		paddingTop: 24,
		paddingBottom: 24,
	},
	sectionContentNoPadding: {
		paddingHorizontal: 0,
		paddingTop: 0,
		paddingBottom: 0,
	},
	kpiOnePagerFullBleed: {
		flex: 1,
	},
	kpiOnePagerFullBleedMobile: {
		marginHorizontal: -24,
	},
	headerRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: 16,
		flexWrap: 'wrap',
		marginBottom: 8,
	},
	tabBar: {
		position: 'relative',
		zIndex: 20,
		elevation: 0,
		shadowColor: 'transparent',
		backgroundColor: 'transparent',
		marginBottom: 0,
		borderBottomWidth: 2,
		borderBottomColor: '#E0E0E0',
		marginHorizontal:24,
		paddingHorizontal: 6,
	},
	tabBarContent: {
		flexDirection: 'row',
		paddingRight: 8,
	},
	tab: {
		paddingRight: 18,
		paddingTop: 0,
		paddingBottom: 10,
		borderBottomWidth: 3,
		borderBottomColor: 'transparent',
		marginBottom: -3,
	},
	tabActive: {
		borderBottomColor: '#005CBC',
	},
	tabLabel: {
		fontSize: 13,
		fontFamily: FontFamily.bold,
		fontWeight: '700',
		color: '#6B6E72',
		lineHeight: 18,
	},
	tabLabelActive: {
		color: '#005CBC',
		fontWeight: '700',
		fontFamily: FontFamily.bold,
	},
	tabContent: {
		position: 'relative',
		zIndex: 1,
		minHeight: 180,
		paddingTop: 12,
	},
	titleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		marginBottom: 8,
	},
	title: {
		fontSize: 20,
		fontFamily: FontFamily.bold,
		fontWeight: '700',
		color: '#2D2F33',
	},
	subtitle: {
		fontSize: 14,
		fontFamily: FontFamily.regular,
		color: '#6B6E72',
		lineHeight: 20,
		marginBottom: 24,
	},
	actionsRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	actionButton: {
		minHeight: 40,
		borderRadius: 20,
		paddingHorizontal: 20,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
	},
	exportButton: {
		backgroundColor: 'transparent',
		borderWidth: 2,
		borderColor: '#0050A6',
	},
	exportButtonText: {
		color: '#0050A6',
		fontSize: 14,
		fontFamily: FontFamily.medium,
		fontWeight: '600',
	},
	refreshButton: {
		backgroundColor: '#0050A6',
	},
	refreshButtonText: {
		color: '#FFFFFF',
		fontSize: 14,
		fontFamily: FontFamily.medium,
		fontWeight: '600',
	},
	// ─── Mobile Filter Button (above tabs) ──────────────────────────────
	mobileFilterBtn: {
		flexDirection: 'row',
		alignItems: 'center',
		alignSelf: 'flex-start',
		gap: 6,
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderRadius: 24,
		borderWidth: 1,
		borderColor: '#D0D9E8',
		backgroundColor: '#FFFFFF',
		marginBottom: 12,
	},
	mobileFilterBtnText: {
		fontFamily: FontFamily.medium,
		fontSize: 14,
		color: '#2D2D2D',
	},
	mobileFilterBadge: {
		backgroundColor: '#005CBC',
		borderRadius: 10,
		minWidth: 20,
		height: 20,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: 5,
	},
	mobileFilterBadgeText: {
		fontFamily: FontFamily.bold,
		fontSize: 11,
		color: '#FFFFFF',
	},
});

export default AnalyticsHubDashBoard;
